import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { normalizeBackendUrl } from "@/lib/api";
import { useAuth } from "./auth";
import type { LicenseTrack } from "./track";

export type Plan = "free" | "pro" | "pro_plus";

type TrackPlans = Record<LicenseTrack, Plan>;

interface SubscriptionContextValue {
  plan: Plan;
  plans: TrackPlans;
  getPlanForTrack: (track: LicenseTrack) => Plan;
  loading: boolean;
  isAdmin: boolean;
  refresh: () => Promise<void>;
  startCheckout: (targetPlan: "pro" | "pro_plus", track?: LicenseTrack, interval?: "month" | "year") => Promise<void>;
  openPortal: () => Promise<void>;
}

const DEFAULT_PLANS: TrackPlans = {
  ppl: "pro_plus",
  cpl: "free",
  pstar: "pro_plus",
  roca: "pro_plus",
};
const PLANS_CACHE_KEY = "cpl_sub_plans_cache";

function loadCachedPlans(): TrackPlans | null {
  try {
    const raw = localStorage.getItem(PLANS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TrackPlans;
    const validPlans: Plan[] = ["free", "pro", "pro_plus"];
    if (parsed && typeof parsed === "object" && validPlans.includes(parsed.cpl)) {
      return { ppl: "pro_plus", cpl: parsed.cpl, pstar: "pro_plus", roca: "pro_plus" };
    }
  } catch (_err) {
    // Corrupted cache — return null so a fresh fetch is triggered
  }
  return null;
}

function saveCachedPlans(plans: TrackPlans) {
  try {
    localStorage.setItem(PLANS_CACHE_KEY, JSON.stringify({ cpl: plans.cpl }));
  } catch (err) {
    console.warn("[subscription] Failed to save plan cache:", err);
  }
}

function clearCachedPlans() {
  try {
    localStorage.removeItem(PLANS_CACHE_KEY);
  } catch (err) {
    console.warn("[subscription] Failed to clear plan cache:", err);
  }
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

function safeNavigate(url: string) {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") throw new Error("Non-HTTPS redirect blocked");
    window.location.href = url;
  } catch (_err) {
    throw new Error("Invalid redirect URL returned from server");
  }
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();

  // Derive isAdmin directly from the auth user role — available immediately from localStorage
  const isAdmin = ["admin", "master_admin"].includes(user?.role ?? "");

  const [plans, setPlans] = useState<TrackPlans>(() => {
    if (!isAuthenticated) return DEFAULT_PLANS;
    return loadCachedPlans() ?? DEFAULT_PLANS;
  });
  const [loading, setLoading] = useState(() => {
    if (!isAuthenticated) return false;
    return loadCachedPlans() === null;
  });
  const baseUrl = normalizeBackendUrl(import.meta.env.VITE_BACKEND_URL as string | undefined);
  const applyPlans = useCallback((newPlans: TrackPlans) => {
    setPlans(newPlans);
    saveCachedPlans(newPlans);
  }, []);

  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      clearCachedPlans();
      setPlans(DEFAULT_PLANS);
      setLoading(false);
      return;
    }
    try {
      const token = localStorage.getItem("token");

const res = await fetch(`${baseUrl}/api/stripe/subscription`, {
  headers: token ? { Authorization: `Bearer ${token}` } : {},
  credentials: "include",
});

if (res.status === 401) {
  localStorage.removeItem("token");
  window.location.href = "/";
  return;
}
      if (res.ok) {
        const data = await res.json() as {
          plan?: Plan;
          plans?: Partial<TrackPlans>;
          isAdmin?: boolean;
        };
        if (data.plans) {
          const resolved: TrackPlans = {
            ppl: "pro_plus",
            cpl: data.plans.cpl ?? data.plan ?? "free",
            pstar: "pro_plus",
            roca: "pro_plus",
          };
          applyPlans(resolved);
        } else if (data.plan) {
          applyPlans({ ppl: "pro_plus", cpl: data.plan, pstar: "pro_plus", roca: "pro_plus" });
        }
      }
    } catch (err) {
      console.warn("[subscription] Failed to fetch subscription:", err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, baseUrl, applyPlans]);

  useEffect(() => {
    if (isAuthenticated) {
      const cached = loadCachedPlans();
      if (cached) setPlans(cached);
    }
    fetchSubscription();
  }, [fetchSubscription, isAuthenticated]);

  const getPlanForTrack = useCallback((track: LicenseTrack): Plan => {
    return plans[track] ?? "free";
  }, [plans]);

  const startCheckout = useCallback(async (
    targetPlan: "pro" | "pro_plus",
    track: LicenseTrack = "cpl",
    interval: "month" | "year" = "month",
  ) => {
    const token = localStorage.getItem("token");
    const successUrl = `${window.location.origin}/checkout/success`;
    const cancelUrl  = `${window.location.origin}/subscription`;
    const res = await fetch(`${baseUrl}/api/stripe/checkout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: "include",
      body: JSON.stringify({ plan: targetPlan, track, interval, successUrl, cancelUrl }),
    });
    let data: { url?: string; error?: string } = {};
    try { data = await res.json(); } catch (err) { console.warn("[subscription] checkout: failed to parse response JSON:", err); }
    if (data.url) {
      safeNavigate(data.url);
    } else {
      throw new Error(data.error ?? (res.ok ? "Could not start checkout" : `Server error ${res.status}. Please try again.`));
    }
  }, [baseUrl]);

  const openPortal = useCallback(async () => {
    const token = localStorage.getItem("token");
    const returnUrl = `${window.location.origin}/subscription`;
    const res = await fetch(`${baseUrl}/api/stripe/portal`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: "include",
      body: JSON.stringify({ returnUrl }),
    });
    let data: { url?: string; error?: string } = {};
    try { data = await res.json(); } catch (err) { console.warn("[subscription] portal: failed to parse response JSON:", err); }
    if (data.url) {
      safeNavigate(data.url);
    } else {
      throw new Error(data.error ?? (res.ok ? "Could not open billing portal" : `Server error ${res.status}. Please try again.`));
    }
  }, [baseUrl]);

  return (
    <SubscriptionContext.Provider value={{
      plan: plans.cpl,
      plans,
      getPlanForTrack,
      loading,
      isAdmin,
      refresh: fetchSubscription,
      startCheckout,
      openPortal,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error("useSubscription must be used inside SubscriptionProvider");
  return ctx;
}
